import { dateOfBirthAdultSchema, dateOfBirthSchema, emailSchema, isValidDocNumber, optionalDate } from '@/utils/schemas'
import { z } from 'zod'
import { UserStatus } from '../auth/auth'
import { Genre } from '../clients/client'
import { IvaCondition } from '../invoices/invoice'
import { DocType } from '../shared'

export const contactInfoSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  genre: z.enum(Object.keys(Genre), { message: 'El género es requerido' }),
  dateOfBirth: dateOfBirthSchema,
  relationship: z.string().min(1, 'La relación es requerida'),
  phone: z.string().min(1, 'El número de teléfono es requerido').optional().or(z.literal('')),
  email: emailSchema.optional().or(z.literal('')),
  street: z.string().optional().or(z.literal('')),
  city: z.string().optional().or(z.literal('')),
  state: z.string().optional().or(z.literal('')),
  zipCode: z.string().optional().or(z.literal('')),
  country: z.string().optional().or(z.literal('')),
  observations: z.string().optional().or(z.literal('')),
})

export const userDataSchema = z.object({
  legalName: z.string().min(1, 'El nombre es requerido'),
  genre: z.enum(Object.keys(Genre), { message: 'El género es requerido' }),
  docType: z.enum(Object.values(DocType), { message: 'Debes seleccionar un tipo de documento' }),
  docNumber: z.string().min(1, 'El número de documento es requerido'),
  invoiceLegalName: z.string().optional(),
  invoiceDocType: z.enum(Object.values(DocType) as any).optional(),
  invoiceDocNumber: z.string().optional(),
  invoiceAddress: z.string().optional(),
  ivaCondition: z.enum(Object.values(IvaCondition), { message: "La condición IVA es requerida" }),
  paidByUserId: z.string().optional(),
  dateOfBirth: optionalDate,
  phone: z.string().min(7, 'El número de teléfono requiere 7 caracteres'),
  email: emailSchema.optional().or(z.literal('')),
  street: z.string().min(1, 'La Dirección es requerida'),
  city: z.string().optional().or(z.literal('')),
  state: z.string().optional().or(z.literal('')),
  zipCode: z.string().optional().or(z.literal('')),
  country: z.string().optional().or(z.literal('')),
  observations: z.string().optional().or(z.literal('')),
}).refine(
  (data) => isValidDocNumber(data.docType, data.docNumber), {
  message: 'El número de documento es inválido para el tipo de documento seleccionado',
  path: ['docNumber']
})

export const createClientSchema = z.object({
  userData: userDataSchema,
  invoiceSameAsHolder: z.boolean().default(true),
  hasReference1: z.boolean(),
  reference1: z.any().optional(),
  hasReference2: z.boolean(),
  reference2: z.any().optional(),
}).superRefine((data, ctx) => {
  if (data.hasReference1) {
    const result = contactInfoSchema.safeParse(data.reference1)
    if (!result.success) {
      result.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['reference1', ...issue.path],
        })
      })
    }
  }
  if (data.hasReference2) {
    const result = contactInfoSchema.safeParse(data.reference2)
    if (!result.success) {
      result.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['reference2', ...issue.path],
        })
      })
    }
  }
})

export type CreateClientDto = z.infer<typeof createClientSchema>
export type ContactInfoDto = z.infer<typeof contactInfoSchema>
export type UserDataDto = z.infer<typeof userDataSchema>

export const invoiceDataSchema = z.object({
  invoiceLegalName: z.string().optional(),
  invoiceDocType: z.enum(Object.values(DocType) as any).optional(),
  invoiceDocNumber: z.string().optional(),
  invoiceAddress: z.string().min(1, 'La dirección de facturación es requerida').optional().or(z.literal('')),
  ivaCondition: z.enum(Object.values(IvaCondition), { message: "La condición IVA es requerida" }).optional(),
  paidByUserId: z.string().optional(),
})

export const updateClientSchema = (tab: string) => {
  return z.object({
    email: emailSchema.optional(),
    status: z.enum(Object.values(UserStatus)).optional(),
    userData: tab === 'titular' ? userDataSchema.optional()
      : tab === 'invoice_data' ? invoiceDataSchema.optional()
        : z.any().optional(),
    reference1: tab === 'ref1' ? contactInfoSchema.optional() : z.any().optional(),
    reference2: tab === 'ref2' ? contactInfoSchema.optional() : z.any().optional(),
  })
}
